import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  MoreVertical, 
  Calendar, 
  User, 
  Tag, 
  MessageSquare, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  Star, 
  Archive, 
  Share2, 
  Copy, 
  ExternalLink,
  ChevronRight,
  ChevronDown,
  Folder,
  CheckSquare,
  Play,
  Pause,
  Zap,
  Target,
  TrendingUp,
  BarChart3,
  Activity,
  Award,
  Lightbulb,
  Settings,
  Eye,
  Link,
  FileText,
  Users,
  GitBranch,
  GitCommit,
  GitPullRequest
} from 'lucide-react';
import { Project, Task } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

interface ProjectDetailProps {
  project: Project;
  tasks: Task[];
  onEdit: (project: Project) => void;
  onDelete: (projectId: string) => void;
  onTaskClick: (task: Task) => void;
  onSubProjectClick: (project: Project) => void;
  onAddTask: (projectId: string) => void;
  onAddSubProject: (parentProjectId: string) => void;
  isEditing?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
}

const ProjectDetail: React.FC<ProjectDetailProps> = ({
  project,
  tasks,
  onEdit,
  onDelete,
  onTaskClick,
  onSubProjectClick,
  onAddTask,
  onAddSubProject,
  isEditing = false,
  onSave,
  onCancel
}) => {
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    tasks: true,
    subProjects: true,
    activity: false,
    settings: false
  });
  
  const [projectData, setProjectData] = useState({
    name: project.name,
    description: project.description || '',
    priority: project.priority,
    due_date: project.due_date || '',
    status: project.status
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'from-green-500 to-emerald-600';
      case 'completed': return 'from-blue-500 to-indigo-600';
      case 'on-hold': return 'from-yellow-500 to-orange-600';
      case 'cancelled': return 'from-red-500 to-pink-600';
      default: return 'from-gray-500 to-slate-600';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'from-red-500 to-pink-600';
      case 'medium': return 'from-yellow-500 to-orange-600';
      case 'low': return 'from-green-500 to-emerald-600';
      default: return 'from-gray-500 to-slate-600';
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const projectTasks = tasks.filter(task => task.project_id === project.id);
  // TODO: Get sub-projects from props or context
  const subProjects: Project[] = [];

  return (
    <div className="p-6 space-y-6">
      {/* Project Header */}
      <div className="space-y-4">
        {isEditing ? (
          <div className="space-y-4">
            <input
              type="text"
              value={projectData.name}
              onChange={(e) => setProjectData(prev => ({ ...prev, name: e.target.value }))}
              className="text-2xl font-bold bg-transparent border-b border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none w-full"
            />
            <textarea
              value={projectData.description}
              onChange={(e) => setProjectData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
              placeholder="Add project description..."
              rows={3}
            />
            <div className="flex items-center space-x-4">
              <select
                value={projectData.priority}
                onChange={(e) => setProjectData(prev => ({ ...prev, priority: e.target.value as 'low' | 'medium' | 'high' }))}
                className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
              </select>
              <select
                value={projectData.status}
                onChange={(e) => setProjectData(prev => ({ ...prev, status: e.target.value as 'active' | 'completed' | 'on-hold' | 'cancelled' }))}
                className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="on-hold">On Hold</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <input
                type="date"
                value={projectData.due_date}
                onChange={(e) => setProjectData(prev => ({ ...prev, due_date: e.target.value }))}
                className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              />
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onSave}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={onCancel}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  {project.name}
                </h1>
                {project.description && (
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    {project.description}
                  </p>
                )}
                <div className="flex items-center space-x-4 text-sm">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${getStatusColor(project.status)} text-white`}>
                    {project.status}
                  </span>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${getPriorityColor(project.priority)} text-white`}>
                    {project.priority} priority
                  </span>
                  {project.due_date && (
                    <span className="flex items-center text-slate-600 dark:text-slate-400">
                      <Calendar className="w-4 h-4 mr-1" />
                      Due {new Date(project.due_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onEdit(project)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  title="Edit project"
                >
                  <Edit className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                </button>
                <button
                  onClick={() => onDelete(project.id)}
                  className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                  title="Delete project"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
                <button
                  onClick={async () => {
                    try {
                      await api.post('/api/ai/enrich-project-tasks', { projectId: project.id, note: project.name });
                      alert('Enrichment requested. Task descriptions will update shortly.');
                    } catch (e) {
                      alert('Failed to enrich descriptions.');
                    }
                  }}
                  className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                  title="Enrich task descriptions"
                >
                  <FileText className="w-4 h-4 text-blue-600" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Project Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckSquare className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Total Tasks</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{projectTasks.length}</p>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Completed</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {projectTasks.filter(t => t.status === 'completed').length}
          </p>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Folder className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Sub Projects</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{subProjects.length}</p>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Progress</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {projectTasks.length > 0 
              ? Math.round((projectTasks.filter(t => t.status === 'completed').length / projectTasks.length) * 100)
              : 0}%
          </p>
        </div>
      </div>

      {/* Tasks Section */}
      <div className="bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600/50"
          onClick={() => toggleSection('tasks')}
        >
          <div className="flex items-center space-x-3">
            <CheckSquare className="w-5 h-5 text-blue-500" />
            <h3 className="font-semibold text-slate-900 dark:text-white">Tasks</h3>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              ({projectTasks.length})
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => { e.stopPropagation(); onAddTask(project.id); }}
              className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
              title="Add task"
            >
              <Plus className="w-4 h-4 text-blue-600" />
            </button>
            {expandedSections.tasks ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </div>
        
        {expandedSections.tasks && (
          <div className="border-t border-slate-200 dark:border-slate-600">
            {projectTasks.length === 0 ? (
              <div className="p-6 text-center text-slate-500 dark:text-slate-400">
                <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tasks yet</p>
                <button
                  onClick={() => onAddTask(project.id)}
                  className="mt-3 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  Add First Task
                </button>
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-slate-600">
                {projectTasks.map((task) => (
                  <div
                    key={task.id}
                    className="p-4 hover:bg-slate-50 dark:hover:bg-slate-600/50 cursor-pointer transition-colors"
                    onClick={() => onTaskClick(task)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          task.status === 'completed' ? 'bg-green-500' :
                          task.status === 'in-progress' ? 'bg-blue-500' :
                          task.status === 'overdue' ? 'bg-red-500' : 'bg-slate-300'
                        }`} />
                        <div>
                          <h4 className="font-medium text-slate-900 dark:text-white">
                            {task.title}
                          </h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {task.assigned_user_name || 'Unassigned'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          task.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        }`}>
                          {task.priority}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sub Projects Section */}
      <div className="bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600/50"
          onClick={() => toggleSection('subProjects')}
        >
          <div className="flex items-center space-x-3">
            <Folder className="w-5 h-5 text-purple-500" />
            <h3 className="font-semibold text-slate-900 dark:text-white">Sub Projects</h3>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              ({subProjects.length})
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => { e.stopPropagation(); onAddSubProject(project.id); }}
              className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
              title="Add sub project"
            >
              <Plus className="w-4 h-4 text-purple-600" />
            </button>
            {expandedSections.subProjects ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </div>
        
        {expandedSections.subProjects && (
          <div className="border-t border-slate-200 dark:border-slate-600">
            {subProjects.length === 0 ? (
              <div className="p-6 text-center text-slate-500 dark:text-slate-400">
                <Folder className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No sub projects yet</p>
                <button
                  onClick={() => onAddSubProject(project.id)}
                  className="mt-3 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
                >
                  Add Sub Project
                </button>
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-slate-600">
                {subProjects.map((subProject) => (
                  <div
                    key={subProject.id}
                    className="p-4 hover:bg-slate-50 dark:hover:bg-slate-600/50 cursor-pointer transition-colors"
                    onClick={() => onSubProjectClick(subProject)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Folder className="w-4 h-4 text-purple-500" />
                        <div>
                          <h4 className="font-medium text-slate-900 dark:text-white">
                            {subProject.name}
                          </h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {subProject.description || 'No description'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${getStatusColor(subProject.status)} text-white`}>
                          {subProject.status}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Activity Section */}
      <div className="bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600/50"
          onClick={() => toggleSection('activity')}
        >
          <div className="flex items-center space-x-3">
            <Activity className="w-5 h-5 text-green-500" />
            <h3 className="font-semibold text-slate-900 dark:text-white">Recent Activity</h3>
          </div>
          {expandedSections.activity ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </div>
        
        {expandedSections.activity && (
          <div className="border-t border-slate-200 dark:border-slate-600 p-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-sm">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-slate-600 dark:text-slate-400">Project created</span>
                <span className="text-slate-400">2 days ago</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-slate-600 dark:text-slate-400">First task added</span>
                <span className="text-slate-400">1 day ago</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
